'use client'

// Tasks section — the played sequence: chips, the date-sorted list, and the
// detail pane that streams the agent's reasoning. Ported from
// apps/frontend/design/legix-website-v2/Website.dc.html (TASKS SECTION block
// of the data-dc-script + the `.ts-sec` template). See
// public/landing/tasks/stream.js for the editorial notes (phases, holds,
// beats) — the data files ship verbatim from the artifact.
//
// Three phases: a fast RUN of bursts, then holds that stream their reasoning
// (phase 2 opens at the first hold and stays open), then the collapse — the
// handled rows fall away and the pane resolves to the six that need a person,
// which become pickable (THE ONE INTERACTIVE MOMENT, D45).

import { Component } from 'react'
import {
  type PaneMetrics,
  type Seed,
  type SeedTxn,
  type StreamEntry,
  type Token,
  Timers,
  acctName,
  paneMetrics,
  prefersReduced,
  rparts,
  scopeLabel,
  seed,
  streamEntries,
  taskMem,
  tokenize,
  units,
} from './landing-live'

const STREAM_CHAR_MS = 5
const ENTRY_GAP_MS = 2800
const BURST_MS = 190
const SLOW_BURST_MS = 520
const STEP_GAP_MS = 320
const COLLAPSE_MS = 520

interface Prog {
  id: string
  bi: number
  wi: number // -1 = whole block
}

interface TasksState {
  started: boolean
  phase: 'idle' | 'playing' | 'done'
  postedIds: string[] // transactions that left the holding account
  correctedIds: string[] // …and were then given a second revision by a person
  flagIds: string[] // the ones the agent declined to place
  slow: boolean // phase 2: the first hold has been reached
  collapsing: boolean // phase 3, mid-animation
  collapsed: boolean // phase 3: only the residue remains on screen
  tid: string | null // THE SUBJECT. Which transaction the right pane is showing.
  shown: number // how many of its lifecycle steps have landed
  entryId: string | null // the stream entry supplying the reasoning, if any
  thinkingId: string | null
  prog: Prog | null // streaming progress
}

interface Act {
  k: string
  mode: 'sys' | 'agent' | 'team'
  type: 'plain' | 'to'
  actor: string
  text: string
  to: string
  amt: string
  time: string
  pillCls: string
}

interface Block {
  isProse: boolean
  isQuote: boolean
  isEvidence: boolean
  scopeLabel?: string
  tokens?: Token[]
  label?: string
  text?: string
  hasTo?: boolean
  to?: string
}

interface ActOpts {
  actor?: string
  text?: string
  to?: string
  amt?: string
  time?: string
  pillCls?: string
}

export class TasksSection extends Component<Record<string, never>, TasksState> {
  override state: TasksState = {
    started: false,
    phase: 'idle',
    postedIds: [],
    correctedIds: [],
    flagIds: [],
    slow: false,
    collapsing: false,
    collapsed: false,
    tid: null,
    shown: 0,
    entryId: null,
    thinkingId: null,
    prog: null,
  }

  private timers = new Timers()
  private metrics: Record<string, PaneMetrics> = {}
  // The authoritative sets live here, not in state. Bursts land ~190ms apart
  // and a deferred write reading this.state would race the one before it,
  // silently dropping rows from the count. State mirrors these.
  private posted: string[] = []
  private corrected: string[] = []
  private flags: string[] = []
  private slow = false
  private playCache: { t: SeedTxn; e: StreamEntry }[] | null = null
  private reduced = false
  private io: IntersectionObserver | null = null
  private onResize: (() => void) | null = null
  private rz: ReturnType<typeof setTimeout> | undefined

  override componentDidMount(): void {
    this.reduced = prefersReduced()
    this.onResize = () => {
      clearTimeout(this.rz)
      this.rz = setTimeout(() => this.forceUpdate(), 120)
    }
    window.addEventListener('resize', this.onResize)
    const node = document.querySelector('.ts-unit') || document.querySelector('.ts-sec')
    // start on visibility, not on load: a one-time build-up fired at load plays
    // to an empty room
    if (!node || typeof IntersectionObserver === 'undefined') {
      this.begin()
      return
    }
    this.io = new IntersectionObserver(
      (ents) => {
        for (const e of ents) {
          if (e.isIntersecting) {
            this.io?.disconnect()
            this.io = null
            this.begin()
            break
          }
        }
      },
      { threshold: 0.25 },
    )
    this.io.observe(node)
  }

  override componentWillUnmount(): void {
    this.timers.clear()
    if (this.io) {
      this.io.disconnect()
      this.io = null
    }
    if (this.onResize) window.removeEventListener('resize', this.onResize)
    clearTimeout(this.rz)
  }

  private at(ms: number, fn: () => void): void {
    this.timers.at(ms, fn)
  }

  private txn(id: string): SeedTxn | null {
    const D = seed()
    return D ? (D.transactions.find((t) => t.id === id) ?? null) : null
  }

  private mem(id: string) {
    const M = taskMem()
    return M ? M.get(id) : null
  }

  private isHold(e: StreamEntry | null): boolean {
    return !!e && e.beat === 'hold'
  }

  // ---------- the played sequence ----------
  private begin(): void {
    if (this.state.started) return
    // The data scripts are separate requests, so componentDidMount can win the
    // race against them.
    if (!seed() || streamEntries().length === 0) {
      this.at(60, () => this.begin())
      return
    }
    this.setState({ started: true, phase: 'playing' })
    this.at(900, () => this.step(0))
  }

  // THE PLAY LIST. Every transaction once, in the order its row appears, each
  // paired with the entry that acts on it. t112 appears twice because two
  // entries act on it — the agent's posting and Maria's correction — and they
  // run back to back on the same row.
  private playList(): { t: SeedTxn; e: StreamEntry }[] {
    if (this.playCache) return this.playCache
    const D = seed()
    if (!D) return []
    const byTxn: Record<string, StreamEntry[]> = {}
    streamEntries().forEach((e) => {
      if (e.kind === 'close') return
      ;(e.txns || []).forEach((id) => {
        ;(byTxn[id] = byTxn[id] || []).push(e)
      })
    })
    const out: { t: SeedTxn; e: StreamEntry }[] = []
    D.transactions
      .slice()
      .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.id < b.id ? -1 : 1))
      .forEach((t) => {
        ;(byTxn[t.id] || []).forEach((e) => out.push({ t, e }))
      })
    this.playCache = out
    return out
  }

  private step(i: number): void {
    const list = this.playList()
    if (i >= list.length) {
      this.finish()
      return
    }
    const pair = list[i]
    const next = () => this.step(i + 1)
    if (this.isHold(pair.e)) {
      // PHASE 2 opens at the first hold, and stays open: the bursts after it
      // slow down too.
      this.slow = true
      if (!this.state.slow) this.setState({ slow: true })
      this.showHold(pair.t, pair.e, () => this.at(ENTRY_GAP_MS, next))
      return
    }
    this.showBurst(pair.t, pair.e, next)
  }

  // PHASE 3. The handled rows fall away in one move and the pane resolves to
  // the six that need a person.
  private finish(): void {
    const e = streamEntries().find((x) => x.kind === 'close')
    // tid goes null, so the track travels back to the top while the rows collapse
    this.setState({ collapsing: true, tid: null })
    this.at(this.reduced ? 0 : COLLAPSE_MS, () => {
      this.setState({ collapsing: false, collapsed: true })
      if (!e) {
        this.setState({ phase: 'done' })
        return
      }
      this.runClose(e, () => this.setState({ phase: 'done' }))
    })
  }

  // THE ONE INTERACTIVE MOMENT (D45). In phase 3, and only there, the six rows
  // become pickable. No streaming on a pick: a click should answer immediately.
  private pick(tid: string): void {
    if (!this.state.collapsed) return
    const e = streamEntries().find((x) => x.kind !== 'close' && (x.txns || []).indexOf(tid) > -1)
    const t = this.txn(tid)
    if (!t) return
    this.setState({
      tid,
      entryId: e ? e.id : null,
      thinkingId: null,
      prog: null,
      shown: this.stepsFor(t, e ?? null).length,
    })
  }

  // BURST. Same pane, same shape, filled instantly. No reasoning.
  private showBurst(t: SeedTxn, e: StreamEntry, done: () => void): void {
    const steps = this.stepsFor(t, e)
    this.applyTxn(t, e)
    this.setState({ tid: t.id, entryId: null, thinkingId: null, prog: null, shown: steps.length })
    this.at(this.slow ? SLOW_BURST_MS : BURST_MS, done)
  }

  // HOLD. The reasoning streams, then the lifecycle lands a step at a time. If
  // the pane is already on this transaction the revealed steps STAY — the
  // correction beat: s108 inherits s107's three steps and adds Maria's as a
  // fourth.
  private showHold(t: SeedTxn, e: StreamEntry, done: () => void): void {
    const same = this.state.tid === t.id
    // The first two steps are HISTORY, on screen from the moment the pane opens.
    const from = same ? this.state.shown : Math.min(2, this.stepsFor(t, e).length)
    const land = () => this.revealSteps(t, e, from, done)
    if (this.reduced) {
      this.setState({ tid: t.id, entryId: e.id, thinkingId: null, prog: null, shown: from })
      this.at(1200, land)
      return
    }
    this.setState({ tid: t.id, entryId: e.id, thinkingId: e.id, prog: null, shown: from })
    this.at(1050, () => this.streamBlocks(e, 0, land))
  }

  private revealSteps(t: SeedTxn, e: StreamEntry, at: number, done: () => void): void {
    const steps = this.stepsFor(t, e)
    if (at >= steps.length) {
      // THE LOOP CLOSING. The row fills in on the last step, so the posting is
      // the consequence of the argument rather than something that happened
      // beside it.
      this.applyTxn(t, e)
      this.at(300, done)
      return
    }
    this.setState({ shown: at + 1 })
    this.at(this.reduced ? 60 : STEP_GAP_MS, () => this.revealSteps(t, e, at + 1, done))
  }

  // The close has no subject: it is about the six that stayed. Reasoning only.
  private runClose(e: StreamEntry, done: () => void): void {
    this.setState({ tid: null, entryId: e.id, thinkingId: e.id, prog: null, shown: 0 })
    this.at(900, () => this.streamBlocks(e, 0, done))
  }

  private applyTxn(t: SeedTxn, entry: StreamEntry): void {
    if (t.state === 'needsyou') {
      if (this.flags.indexOf(t.id) === -1) this.flags.push(t.id)
    } else if (entry.kind === 'corrected') {
      if (this.corrected.indexOf(t.id) === -1) this.corrected.push(t.id)
    } else if (this.posted.indexOf(t.id) === -1) {
      this.posted.push(t.id)
    }
    this.setState({
      postedIds: this.posted.slice(),
      correctedIds: this.corrected.slice(),
      flagIds: this.flags.slice(),
    })
  }

  // ---------- the lifecycle ----------
  // Derived from the transaction every frame, never accumulated.
  private stepsFor(t: SeedTxn, e: StreamEntry | null): Act[] {
    const D = seed()
    if (!D) return []
    const CODE = 'cl-pill cl-pill--code'
    const FLAG = 'cl-pill cl-pill--flag'
    const out: Act[] = [
      this.act('rec', 'sys', 'plain', { text: 'Entry recorded from the bank feed', time: t.date }),
      this.act('add', 'sys', 'to', {
        text: 'Auto-added to',
        to: D.label(t.from),
        pillCls: CODE,
        time: t.date,
      }),
    ]
    if (t.state === 'needsyou') {
      out.push(
        this.act('flag', 'agent', 'to', {
          actor: 'Agent',
          text: 'flagged Maria',
          to: t.reason || 'Needs you',
          pillCls: FLAG,
          time: 'just now',
        }),
      )
      return out
    }
    if (t.split) {
      // the split legs — the one thing the retired change card carried that a
      // single row cannot
      out.push(
        this.act('sp', 'agent', 'plain', {
          actor: 'Agent',
          text: 'split it two ways',
          time: 'just now',
        }),
      )
      t.split.forEach((s, i) => {
        out.push(
          this.act('sp' + i, 'sys', 'to', {
            text: s.label + ' to',
            to: D.label(s.to),
            amt: D.money(s.amt),
            pillCls: CODE,
            time: 'just now',
          }),
        )
      })
      return out
    }
    out.push(
      this.act('cat', 'agent', 'to', {
        actor: 'Agent',
        text: 'categorized it as',
        to: D.label(t.to || ''),
        pillCls: CODE,
        time: 'just now',
      }),
    )
    // The correction stacks on top. The agent's original stays exactly where it was.
    const corrected = (e && e.kind === 'corrected') || this.state.correctedIds.indexOf(t.id) > -1
    if (corrected && t.correctedTo) {
      out.push(
        this.act('rev', 'team', 'to', {
          actor: this.personFirst(t.correctedBy) || 'The firm',
          text: 'changed it to',
          to: D.label(t.correctedTo),
          pillCls: CODE,
          time: 'just now',
        }),
      )
    }
    return out
  }

  private act(key: string, mode: Act['mode'], type: Act['type'], o: ActOpts): Act {
    return {
      k: key,
      mode,
      type,
      actor: o.actor || '',
      text: o.text || '',
      to: o.to || '',
      amt: o.amt || '',
      time: o.time || '',
      pillCls: o.pillCls || 'cl-pill cl-pill--code',
    }
  }

  // ---------- the reasoning stream ----------
  private streamBlocks(entry: StreamEntry, bi: number, done: () => void): void {
    const parts = rparts(entry)
    if (bi >= parts.length) {
      this.setState({ thinkingId: null, prog: null })
      done()
      return
    }
    const p = parts[bi]
    // An evidence block is a lookup result — it appears whole, the way a query
    // result does.
    if (p.evidence) {
      this.setState({ thinkingId: null, prog: { id: entry.id, bi, wi: -1 } })
      this.at(760, () => this.streamBlocks(entry, bi + 1, done))
      return
    }
    let toks: Token[]
    if (p.cite) {
      const m = this.mem(p.cite)
      toks = tokenize(m ? m.body : '')
    } else {
      toks = [{ v: p.t || '', cls: '' }]
    }
    this.streamWords(entry, bi, units(toks).length, 1, done)
  }

  private streamWords(
    entry: StreamEntry,
    bi: number,
    total: number,
    wi: number,
    done: () => void,
  ): void {
    this.setState({ thinkingId: null, prog: { id: entry.id, bi, wi } })
    if (wi >= total) {
      this.at(300, () => this.streamBlocks(entry, bi + 1, done))
      return
    }
    // SIX characters a tick keeps renders to ~one per frame at the same
    // apparent speed.
    const step = Math.min(total - wi, 6)
    this.at(STREAM_CHAR_MS * step + Math.random() * STREAM_CHAR_MS, () =>
      this.streamWords(entry, bi, total, wi + step, done),
    )
  }

  // ---------- labels ----------
  private personFirst(id: string | undefined): string {
    const D = seed()
    const p = D && (D.staff || []).find((x) => x.id === id)
    return p ? p.first : ''
  }

  private kindLabel(t: SeedTxn | null, e: StreamEntry | null): string {
    if (e && e.kind === 'close') return 'Result'
    if (!t) return this.state.phase === 'idle' ? 'Idle' : 'Working'
    if (this.state.correctedIds.indexOf(t.id) > -1 || (e && e.kind === 'corrected'))
      return 'Corrected'
    if (t.state === 'needsyou') return 'Needs you'
    return 'Applied'
  }

  // Stated once, before there is a transaction to inspect: where these 32
  // things came from and why they are all in one pile. Counted from the rows,
  // like everything else.
  private openingSteps(D: Seed): Act[] {
    const dates = D.transactions.map((x) => x.date).sort()
    const n = D.transactions.length
    return [
      this.act('boot:feed', 'sys', 'plain', {
        text: 'Bank feed imported, ' + n + ' entries',
        time: dates[0] + ' to ' + dates[dates.length - 1],
      }),
      this.act('boot:hold', 'sys', 'plain', {
        text: 'None could be categorized on arrival, so all ' + n + ' landed in a holding account',
        time: D.waves.length + ' accounts',
      }),
    ]
  }

  // Evidence: a lookup result rather than a written rule. Any account named
  // goes through `to` so it resolves by reference, same rule as the quotes.
  private evidenceBlock(D: Seed, ev: NonNullable<StreamEntry['parts']>[number]['evidence']): Block {
    return {
      isProse: false,
      isQuote: false,
      isEvidence: true,
      label: ev?.label || 'Evidence',
      text: ev?.text || '',
      hasTo: !!ev?.to,
      to: ev?.to ? D.label(ev.to) : '',
    }
  }

  override render() {
    const S = this.state
    const D = seed()

    const title = 'Cleared by default. Not a queue waiting for you.'
    const sub =
      'Your firm’s judgment is applied on any holding account. ' +
      'Audit what happened, see the reasoning, and teach the system.'

    /* ---- the chips: each holds the live sum of its own un-posted rows ---- */
    const closeE = S.entryId ? streamEntries().find((x) => x.id === S.entryId) : undefined
    const atClose = S.phase === 'done' || !!(closeE && closeE.kind === 'close')
    const chips = D
      ? D.waves.map((w) => {
          const a = D.acct(w.account)
          const all = D.inWave(w.n)
          const left = all.filter((t) => S.postedIds.indexOf(t.id) === -1)
          const openAmt = D.sum(all)
          const leftAmt = D.sum(left)
          const flagged = left.filter((t) => S.flagIds.indexOf(t.id) > -1)
          const done = left.length > 0 && left.length === flagged.length
          const pct = openAmt > 0 ? Math.round(((openAmt - leftAmt) / openAmt) * 1000) / 10 : 0
          let cls = 'ts-chip'
          if (!left.length) cls += ' is-done'
          else if (done) cls += ' is-flagged'
          if (atClose) cls += ' is-dim'
          let meta: string
          if (!left.length) meta = 'cleared'
          else if (done)
            meta = flagged.length === 1 ? '1 left for you' : flagged.length + ' left for you'
          else meta = left.length + (left.length === 1 ? ' item' : ' items')
          return {
            cls,
            no: a ? a.no : w.account,
            name: a ? a.name : w.account,
            amt: '$' + D.money(leftAmt),
            meta,
            barStyle: { width: pct + '%' },
          }
        })
      : []
    const anyCls = 'ts-chip ts-chip--any' + (atClose ? ' is-lit' : '')

    /* ---- the list: one date-sorted pile; rows mute, they do not leave ---- */
    const rows = D
      ? D.transactions
          .filter((t) => !S.collapsed || S.flagIds.indexOf(t.id) > -1)
          .slice()
          .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.id < b.id ? -1 : 1))
          .map((t) => {
            const isFlag = S.flagIds.indexOf(t.id) > -1
            const isCorr = S.correctedIds.indexOf(t.id) > -1
            const isDone = S.postedIds.indexOf(t.id) > -1
            let cls = 'ts-row'
            let chip = ''
            if (isFlag) {
              cls += ' is-flag'
              chip = t.reason || 'Needs you'
            } else if (isCorr) {
              cls += ' is-handled'
              chip = acctName(D, t.correctedTo || '')
            } else if (isDone) {
              cls += ' is-handled'
              chip = t.split ? 'Split across 2 accounts' : acctName(D, t.to || '')
            } else cls += ' is-pending'
            if (S.tid === t.id) cls += ' is-current'
            if (S.collapsing && !isFlag) cls += ' is-collapsing'
            if (S.collapsed) cls += ' is-pick'
            return {
              cls,
              tid: t.id,
              date: t.date,
              raw: t.raw,
              src: t.from,
              chip,
              amt: D.money(t.amt),
              pickable: S.collapsed,
            }
          })
      : []

    /* THE TRACK: an inner element is translated; the current row parks ~40% down */
    let shift = 0
    const m = paneMetrics(this.metrics, '.ts-unit .ts-rows')
    if (m && S.tid && !S.collapsed) {
      let idx = -1
      for (let k = 0; k < rows.length; k++)
        if (rows[k].tid === S.tid) {
          idx = k
          break
        }
      if (idx > -1)
        shift = Math.max(0, Math.min(rows.length * m.row - m.box, idx * m.row - m.box * 0.42))
    }

    /* ---- the right pane: ONE transaction ---- */
    const t = S.tid ? this.txn(S.tid) : null
    const e = S.entryId ? (streamEntries().find((x) => x.id === S.entryId) ?? null) : null
    const isClose = !!e && e.kind === 'close'
    const thinking = !!e && S.thinkingId === e.id
    const prog = e && S.prog && S.prog.id === e.id ? S.prog : null

    const blocks: Block[] = []
    if (D && e && !thinking) {
      const parts = rparts(e)
      for (let pi = 0; pi < parts.length; pi++) {
        const p = parts[pi]
        if (prog && pi > prog.bi) break
        const partial = !!prog && pi === prog.bi && prog.wi >= 0
        if (p.evidence) {
          blocks.push(this.evidenceBlock(D, p.evidence))
        } else if (p.cite) {
          const m2 = this.mem(p.cite)
          const full = tokenize(m2 ? m2.body : '')
          blocks.push({
            isQuote: true,
            isProse: false,
            isEvidence: false,
            scopeLabel: scopeLabel(D, m2),
            tokens:
              partial && prog
                ? units(full)
                    .slice(0, prog.wi)
                    .concat([{ v: '', cls: 'ts-scaret' }])
                : full,
          })
        } else if (partial && prog) {
          blocks.push({
            isQuote: false,
            isProse: true,
            isEvidence: false,
            scopeLabel: '',
            tokens: units([{ v: p.t || '', cls: '' }])
              .slice(0, prog.wi)
              .concat([{ v: '', cls: 'ts-scaret' }]),
          })
        } else {
          blocks.push({
            isQuote: false,
            isProse: true,
            isEvidence: false,
            scopeLabel: '',
            tokens: [{ v: p.t || '', cls: '' }],
          })
        }
      }
    }

    /* The lifecycle, cut to how much of it has landed. */
    let steps: Act[]
    if (t) steps = this.stepsFor(t, e).slice(0, S.shown)
    else if (isClose || !D) steps = []
    else steps = this.openingSteps(D)

    const acts = steps.map((a) => ({
      k: a.k,
      cls: 'act-item ts-act--' + a.mode,
      glyph: a.mode === 'team' ? '✎' : a.mode === 'agent' ? '✳' : '',
      isPlain: a.type === 'plain',
      isTo: a.type === 'to',
      actor: a.actor,
      text: a.text,
      to: a.to,
      amt: a.amt,
      time: a.time,
      pillCls: a.pillCls,
    }))

    const total = D ? D.transactions.length : 0
    const nPosted = S.postedIds.length
    const residue = D ? D.transactions.filter((x) => S.flagIds.indexOf(x.id) > -1) : []

    const dPayee = t ? t.payee || 'Unidentified' : D ? D.client.name : ''
    const dAmt = D
      ? t
        ? (t.dir === 'credit' ? '+' : '') + D.money(t.amt)
        : isClose
          ? '$' + D.money(D.sum(residue))
          : '$' + D.money(D.sum(D.transactions))
      : ''
    const dIdent = t
      ? t.date + ' · ' + t.raw
      : isClose
        ? 'Left for a person'
        : D
          ? D.client.trade
          : ''
    const kindCls =
      'ts-kind' +
      (t && S.correctedIds.indexOf(t.id) > -1 ? ' ts-kind--corrected' : '') +
      (t && t.state === 'needsyou' ? ' ts-kind--needsyou' : '')
    const hasReason = !!e
    const hasActivity = !isClose
    const listKicker = 'In the accounts'
    const listCount = nPosted ? nPosted + ' of ' + total + ' posted' : total + ' items'

    return (
      <div style={{ borderTop: '1px solid var(--line)' }}>
        <div className="ts-sec" data-screen-label="Tasks section" data-slot="landing-tasks">
          <header className="ts-head">
            <h2 className="ts-title">{title}</h2>
            <p className="ts-sub">{sub}</p>
          </header>

          <div className="ts-chips">
            {chips.map((c) => (
              <div key={c.no} className={c.cls}>
                <div className="ts-chip-top">
                  <span className="ts-chip-no">{c.no}</span>
                  <span className="ts-chip-amt">{c.amt}</span>
                </div>
                <div className="ts-chip-sub">
                  <span className="ts-chip-name">{c.name}</span>
                  <span className="ts-chip-meta">{c.meta}</span>
                </div>
                <div className="ts-bar">
                  <span className="ts-bar-fill" style={c.barStyle}></span>
                </div>
              </div>
            ))}
            <div className={anyCls}>
              <span className="ts-chip-also">Also</span>
              <span className="ts-chip-anyname">Any holding account</span>
            </div>
          </div>

          <div className="ts-unit">
            <div className="ts-pane ts-pane--list">
              <div className="ts-panehead">
                <span className="ts-kicker">{listKicker}</span>
                <span className="ts-kicker ts-kicker--r">{listCount}</span>
              </div>
              <div className="ts-colhead">
                <span className="ts-ch">Date</span>
                <span className="ts-ch">Bank description</span>
                <span className="ts-ch">Account</span>
                <span className="ts-ch ts-ch--amt">Amount</span>
              </div>
              <div className="ts-rows">
                <div
                  className="ts-rows-in"
                  style={{ transform: 'translateY(-' + Math.round(shift) + 'px)' }}
                >
                  {rows.map((r) => (
                    <div
                      key={r.tid}
                      className={r.cls}
                      data-tid={r.tid}
                      onClick={r.pickable ? () => this.pick(r.tid) : undefined}
                    >
                      <span className="ts-date">{r.date}</span>
                      <span className="ts-raw">{r.raw}</span>
                      <span className="ts-dec">
                        <span className="ts-src">{r.src}</span>
                        <span className="ts-arrow">→</span>
                        <span className="ts-dchip">{r.chip}</span>
                      </span>
                      <span className="ts-amt">{r.amt}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="ts-pane ts-pane--detail">
              <div className="ts-dhead">
                <div className="ts-dtop">
                  <span className="ts-dpayee">{dPayee}</span>
                  <span className={kindCls}>{this.kindLabel(t, e)}</span>
                  <span className="ts-damt">{dAmt}</span>
                </div>
                <div className="ts-dident">{dIdent}</div>
              </div>
              <div className="ts-detail-slot">
                <div className="ts-detail">
                  {hasReason ? (
                    <div className="tx-sec tx-sec--reason">
                      <div className="tx-label">Reasoning</div>
                      {thinking ? (
                        <div className="ts-think">
                          <span className="ts-think-dot"></span>
                          <span className="ts-think-dot"></span>
                          <span className="ts-think-dot"></span>
                        </div>
                      ) : null}
                      {/* index keys: the block list is append-only while an entry
                          streams and resets wholesale between entries */}
                      {blocks.map((b, bi) => {
                        if (b.isProse)
                          return (
                            <p key={bi} className="tx-reason">
                              {(b.tokens ?? []).map((tk, ti) => (
                                <span key={ti} className={tk.cls}>
                                  {tk.v}
                                </span>
                              ))}
                            </p>
                          )
                        if (b.isQuote)
                          return (
                            <div key={bi} className="ts-quote">
                              <span className="ts-quote-scope">{b.scopeLabel}</span>
                              <span className="ts-quote-body">
                                {(b.tokens ?? []).map((tk, ti) => (
                                  <span key={ti} className={tk.cls}>
                                    {tk.v}
                                  </span>
                                ))}
                              </span>
                            </div>
                          )
                        return (
                          <div key={bi} className="ts-eve">
                            <span className="ts-eve-label">{b.label}</span>
                            <span className="ts-eve-text">
                              {b.text}
                              {b.hasTo ? <span className="ts-eve-to">{b.to}</span> : null}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  ) : null}

                  {hasActivity ? (
                    <div className="tx-sec tx-sec--last tx-sec--act">
                      <div className="tx-label">Activity</div>
                      <div className="act-list">
                        {acts.map((a) => (
                          <div key={a.k} className={a.cls}>
                            <span className="act-ico">{a.glyph}</span>
                            <span className="act-content">
                              {a.isPlain ? (
                                <>
                                  <span className="act-actor">{a.actor}</span>
                                  <span className="act-txt">{a.text}</span>
                                </>
                              ) : null}
                              {a.isTo ? (
                                <>
                                  <span className="act-actor">{a.actor}</span>
                                  <span className="act-txt">{a.text}</span>
                                  <span className={a.pillCls}>{a.to}</span>
                                  <span className="act-amt">{a.amt}</span>
                                </>
                              ) : null}
                              <span className="act-sep">·</span>
                              <span className="act-time">{a.time}</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <div className="ts-foot">
            <div className="lgx-fcol">
              <svg
                className="lgx-fic"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 21a9 9 0 1 1 9 -9 M12 7v5l3 2"></path>
              </svg>
              <h3 className="lgx-ft">Fully auditable.</h3>
              <p className="lgx-fd">
                Every actor is tracked. Every change writes a new revision. The value before it
                stays, with who changed it and when.
              </p>
            </div>
            <div className="lgx-fcol">
              <svg
                className="lgx-fic"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 20l4 -1l10 -10a2.1 2.1 0 0 0 -3 -3l-10 10z M13.5 6.5l3 3"></path>
              </svg>
              <h3 className="lgx-ft">Your judgment, applied.</h3>
              <p className="lgx-fd">
                Correct a posting and the rule behind it becomes a memory the agent cites on the
                next one, and on every client it fits.
              </p>
            </div>
            <div className="lgx-fcol">
              <svg
                className="lgx-fic"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 8h13l-3 -3 M20 16h-13l3 3"></path>
              </svg>
              <h3 className="lgx-ft">Works with QBO.</h3>
              <p className="lgx-fd">
                Every posting lands in your client’s QuickBooks, kept in two-way sync.
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }
}
